import '@formatjs/intl-getcanonicallocales/polyfill';
import { AppVersion } from '@layout/AppVersion/AppVersion';
import { i18n, appWithTranslation } from 'next-i18next';
import type { SSRConfig } from 'next-i18next';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import '../styles/global.css';
import '../styles/tippy-tooltip.css';
import { Toasts } from '@hooks/useToasts';
import { useSessionStoreInitializer } from '@hooks/useSessionStoreInitializer';
import Head from 'next/head';
import { PiwikPro } from '@layout/PiwikPro/PiwikPro';
import { useRouter } from 'next/router';
import { GoogleTagManager } from '@layout/GoogleTagManager/GoogleTagManager';
import { getLocale } from '@libs/constants';
import { AbbyProvider, withAbby } from '@libs/abby/abby';
import getConfig from 'next/config';
import type { AppProps } from 'next/app';
import type { ComponentType } from 'react';
import { useState } from 'react';
import type { NextRuntimeConfiguration } from '@libs/config';
import type { Translations } from '@icommerce/i18n';
import { isGtmEnabledForLocale } from '@icommerce/i18n';
import { getPiwikId } from '@icommerce/i18n/src';
import { CountrySwitchContextProvider, NavigationProvider } from '@cms/ui';
import { AbbyDevtools } from '@libs/abby/AbbyDevtools';
import { CadSearchScript } from '@layout/CadSearch/CadSearchScript/CadSearchScript';
import { clientSideNextI18nConfig } from '@libs/i18n.helper';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

const MyApp = ({ Component, pageProps: { __ABBY_PROJECT_DATA__, ...pageProps } }: AppProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            staleTime: Infinity,
            cacheTime: publicRuntimeConfig.useQueryCacheTtl,
          },
          mutations: {
            retry: 0,
          },
        },
      })
  );
  const router = useRouter();
  const locale = getLocale(router);
  const isGtmEnabled = isGtmEnabledForLocale(locale);
  useSessionStoreInitializer();

  // eslint-disable-next-line no-underscore-dangle
  const nextI18Next = (pageProps as SSRConfig)._nextI18Next;

  if (nextI18Next?.userConfig?.resources) {
    // Add resources to the i18n initial store for SSR
    const resources = nextI18Next.userConfig.resources as Translations;
    for (const i18NLocale in resources) {
      if (Object.hasOwn(resources, i18NLocale)) {
        const localResource = resources[i18NLocale] as Translations;
        for (const namespace in localResource) {
          if (Object.hasOwn(localResource, namespace)) {
            i18n.addResourceBundle(i18NLocale, namespace, localResource[namespace]);
          }
        }
      }
    }
  }

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      {getPiwikId(publicRuntimeConfig.envName, locale) && (
        <PiwikPro containerId={getPiwikId(publicRuntimeConfig.envName, locale)} />
      )}
      {isGtmEnabled && <GoogleTagManager id={publicRuntimeConfig.googleTagManagerId} />}
      <QueryClientProvider client={queryClient}>
        <AppVersion />
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */}
        <Hydrate state={pageProps.dehydratedState}>
          <CountrySwitchContextProvider>
            <NavigationProvider>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              <AbbyProvider initialData={__ABBY_PROJECT_DATA__}>
                <CadSearchScript />
                <AbbyDevtools />
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <Component {...pageProps} />
              </AbbyProvider>
            </NavigationProvider>
          </CountrySwitchContextProvider>
        </Hydrate>
        <Toasts />
      </QueryClientProvider>
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default appWithTranslation(withAbby(MyApp) as ComponentType<AppProps<any>>, clientSideNextI18nConfig);
